import { createSlice } from "@reduxjs/toolkit";
import type { BrandSliceTypes } from "../../types/brand/brand.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  CreateBrandResponse,
  GetAllBrandsResponse,
  GetBrandResponse,
  GetBrandsResponse,
  UpdateBrandResponse,
} from "../../types/brand/brand.response";
import type {
  CreateBrandPayload,
  GetAllBrandsPayload,
  GetBrandsPayload,
  UpdateBrandPayload,
} from "../../types/brand/brand.payload";

const initialState: BrandSliceTypes = {
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    all: false,
  },
  error: null,
  brand: null,
  brands: [],
  pagination: null,
  allBrands: [],
};

export const createBrand = createApiThunk<
  CreateBrandResponse,
  CreateBrandPayload
>(
  "brand/createBrand",
  async (data) => (await axiosClient.post(API.brand.create, data)).data,
  "Tạo thương hiệu thất bại",
);

export const updateBrand = createApiThunk<
  UpdateBrandResponse,
  UpdateBrandPayload
>(
  "brand/updateBrand",
  async ({ id, data }) =>
    (await axiosClient.put(API.brand.update(id), data)).data,
  "Sửa thương hiệu thất bại",
);

export const deleteBrand = createApiThunk<string, string>(
  "brand/deleteBrand",
  async (id) => (await axiosClient.delete(API.brand.delete(id))).data,
  "Xóa thương hiệu thất bại",
);

export const getBrands = createApiThunk<GetBrandsResponse, GetBrandsPayload>(
  "brand/getBrands",
  async (query) =>
    (await axiosClient.get(API.brand.list, { params: query })).data,
  "Lấy danh sách thương hiệu thất bại",
);

export const getBrand = createApiThunk<GetBrandResponse, string>(
  "brand/getBrand",
  async (id) => (await axiosClient.get(API.brand.detail(id))).data,
  "Lấy thông tin thương hiệu thất bại",
);

export const getAllBrands = createApiThunk<
  GetAllBrandsResponse,
  GetAllBrandsPayload
>(
  "brand/getAllBrands",
  async (query) =>
    (await axiosClient.get(API.brand.all, { params: query })).data,
  "Lấy tất cả thương hiệu thất bại",
);

const brandSlice = createSlice({
  name: "brand",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: createBrand,
      loadingKey: "create",
      fallbackError: "Tạo thương hiệu thất bại",
      onSuccess: (state, payload) => {
        state.brands.push(payload.brand);
      },
    });
    addAsyncCases({
      builder,
      thunk: updateBrand,
      loadingKey: "update",
      fallbackError: "Sửa thương hiệu thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.updatedBrand;
        const idx = state.brands.findIndex((b) => b._id === updated._id);
        if (idx !== -1) state.brands[idx] = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteBrand,
      loadingKey: "delete",
      fallbackError: "Xóa thương hiệu thất bại",
      onSuccess: (state, payload) => {
        state.brands = state.brands.filter((b) => b._id !== payload);
      },
    });
    addAsyncCases({
      builder,
      thunk: getBrands,
      loadingKey: "list",
      fallbackError: "Lấy danh sách thương hiệu thất bại",
      onSuccess: (state, payload) => {
        state.brands = payload.brands;
      },
    });
    addAsyncCases({
      builder,
      thunk: getBrand,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin thương hiệu thất bại",
      onSuccess: (state, payload) => {
        state.brand = payload.brand;
      },
    });
    addAsyncCases({
      builder,
      thunk: getAllBrands,
      loadingKey: "all",
      fallbackError: "Lấy tất cả thương hiệu thất bại",
      onSuccess: (state, payload) => {
        state.allBrands = payload.brands;
      },
    });
  },
});

export default brandSlice.reducer;
